/**
 * cierreHelpers.ts — Constantes y helpers puros para cierres de caja.
 *
 * Exporta:
 *   - MOTIVOS_MAP          : catálogo de códigos → texto legible
 *   - formatMotivo()       : traduce un código a su texto
 *   - getBalanceStatus()   : retorna label/color/icon según diferencia
 *   - getCajaHours()       : cuántas horas estuvo abierta la caja
 *   - formatCierreDuration(): duración del turno legible ("3h 35min")
 *   - formatCierreRange()  : rango de fecha formateado para mostrar en lista
 *
 * NO debe contener:
 *   - Llamadas a servicios o firebase
 *   - Lógica de componentes React
 *   - Importaciones de Lucide, Tailwind classes directas o JSX
 */

import {
  format,
  isSameDay,
  differenceInHours,
  differenceInMinutes,
} from "date-fns";
import { es } from "date-fns/locale";
import type { CierreCaja } from "../types/caja.types";

// ─── Catálogo de motivos de descuadre ────────────────────────────────────────

export const MOTIVOS_MAP: Record<string, string> = {
  pago_mercancia: "Pago de Mercancía / Proveedor",
  gasto_domicilio: "Pago de Domicilio / Mensajería",
  servicios_publicos: "Pago de Servicios (Agua / Luz / Internet)",
  insumos_aseo: "Compra de Insumos / Aseo / Papelería",
  error_vueltas: "Error al entregar vueltas al cliente",
  billete_falso: "Retiro por Billete Falso",
  venta_no_registrada: "Olvido de registro de venta en sistema",
  dinero_retirado: "Dinero retirado por administración",
  faltante_desconocido: "Faltante (No se sabe la razón)",
  sobrante_desconocido: "Sobrante (Sobró dinero en caja)",
  otros: "Otro motivo",
};

/** Traduce un código de motivo a texto legible. Si no se reconoce devuelve el mismo code. */
export const formatMotivo = (code: string): string => MOTIVOS_MAP[code] || code;

// ─── Estado visual del balance ────────────────────────────────────────────────

export interface BalanceStatus {
  label: string;
  color: string;
  icon: string;
}

export const getBalanceStatus = (dif: number | null): BalanceStatus => {
  if (dif === null)
    return {
      label: "Sin datos",
      color: "bg-gray-100 text-gray-500",
      icon: "—",
    };
  if (dif === 0)
    return {
      label: "Cuadrada",
      color: "bg-green-100 text-green-700",
      icon: "✔",
    };
  const abs = Math.abs(dif);
  // Signo → dirección explícita: sobrante (dinero de más) / faltante (dinero de menos).
  const direccion = dif > 0 ? "Sobrante" : "Faltante";
  if (abs <= 5000)
    return {
      label: `${direccion} menor`,
      color: "bg-amber-100 text-amber-700",
      icon: "⚠",
    };
  return {
    label: direccion,
    color: "bg-red-100 text-red-700",
    icon: dif > 0 ? "🟠" : "🚨",
  };
};

/**
 * Etiqueta corta y explícita para el descuadre NETO consolidado.
 * Reemplaza el ambiguo "Descuadre +/-" por su significado real.
 */
export const getNetDifferenceLabel = (dif: number): string =>
  dif === 0 ? "Cuadrado" : dif > 0 ? "Sobrante" : "Faltante";

// ─── Tiempo de apertura ───────────────────────────────────────────────────────

/** Retorna el número de horas que la caja estuvo / lleva abierta. */
export const getCajaHours = (cierre: CierreCaja): number => {
  const start = new Date(cierre.abiertaEn);
  const end = cierre.cerradaEn ? new Date(cierre.cerradaEn) : new Date();
  return differenceInHours(end, start);
};

/**
 * Duración del turno en formato legible, ej. "3h 35min", "45min" o "2h".
 * Usa cerradaEn si existe; si la caja sigue abierta, calcula contra el momento actual.
 * Devuelve "—" si no hay fecha de apertura válida.
 */
export const formatCierreDuration = (cierre: CierreCaja): string => {
  if (!cierre.abiertaEn) return "—";
  const start = new Date(cierre.abiertaEn);
  const end = cierre.cerradaEn ? new Date(cierre.cerradaEn) : new Date();
  const totalMin = Math.max(0, differenceInMinutes(end, start));
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h > 0 && m > 0) return `${h}h ${m}min`;
  if (h > 0) return `${h}h`;
  return `${m}min`;
};

// ─── Formato de rango de fechas ───────────────────────────────────────────────

/**
 * Devuelve el rango de apertura→cierre como string legible.
 * Si abrió y cerró el mismo día: "19 de marzo, 2026".
 * Si cruzó días: "19 de mar — 20 de mar, 2026".
 */
export const formatCierreRange = (cierre: CierreCaja): string => {
  if (
    !cierre.cerradaEn ||
    (cierre.abiertaEn &&
      cierre.cerradaEn &&
      isSameDay(new Date(cierre.abiertaEn), new Date(cierre.cerradaEn)))
  ) {
    return cierre.abiertaEn
      ? format(new Date(cierre.abiertaEn), "d 'de' MMMM, yyyy", { locale: es })
      : "Fecha no disponible";
  }
  const open = cierre.abiertaEn
    ? format(new Date(cierre.abiertaEn), "d 'de' MMM", { locale: es })
    : "";
  const close = cierre.cerradaEn
    ? format(new Date(cierre.cerradaEn), "d 'de' MMM", { locale: es })
    : "";
  const year = cierre.abiertaEn
    ? format(new Date(cierre.abiertaEn), "yyyy")
    : "";
  return `${open} — ${close}, ${year}`;
};
